import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import i18next from 'i18next';
import { NotificationsService } from '../../api/generated/services/NotificationsService';
import type { NotificationView } from '../../api/generated/models/NotificationView';
import { addNotification } from './notificationSlice';
import type { RootState } from '../store';

interface NotificationFeedState {
  items: NotificationView[];
  unreadCount: number;
  loading: boolean;
  itemsLoaded: boolean;
}

const initialState: NotificationFeedState = {
  items: [],
  unreadCount: 0,
  loading: false,
  itemsLoaded: false,
};

export const fetchNotificationFeed = createAsyncThunk(
  'notificationFeed/fetch',
  async (
    options: { includeItems?: boolean } | undefined,
    { dispatch, getState },
  ) => {
    const includeItems = options?.includeItems !== false;
    try {
      const response = await NotificationsService.getAppNotificationsList(20);
      const state = getState() as RootState;
      return {
        items: includeItems ? (response.items ?? []) : state.notificationFeed.items,
        unreadCount: response.unreadCount ?? 0,
        itemsLoaded: includeItems ? true : state.notificationFeed.itemsLoaded,
      };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load notifications';
      dispatch(addNotification({
        type: 'error',
        title: i18next.t('notification_error'),
        message,
      }));
      throw err;
    }
  },
);

export const fetchNotificationBadge = createAsyncThunk(
  'notificationFeed/fetchBadge',
  async (_, { dispatch }) => {
    try {
      const response = await NotificationsService.getAppNotificationsList(1);
      return { unreadCount: response.unreadCount ?? 0 };
    } catch {
      // Badge is non-critical on background refresh — fail silently.
      return { unreadCount: 0 };
    }
  },
);

export const markNotificationRead = createAsyncThunk(
  'notificationFeed/markRead',
  async (id: string, { dispatch, getState, rejectWithValue }) => {
    const state = getState() as RootState;
    const snapshot = {
      items: state.notificationFeed.items,
      unreadCount: state.notificationFeed.unreadCount,
    };
    dispatch(notificationFeedSlice.actions.applyMarkRead(id));
    try {
      await NotificationsService.postAppNotificationRead(id);
      return id;
    } catch (err) {
      dispatch(notificationFeedSlice.actions.restoreFeed(snapshot));
      const message = err instanceof Error ? err.message : 'Failed to mark notification read';
      dispatch(addNotification({
        type: 'error',
        title: i18next.t('notification_error'),
        message,
      }));
      return rejectWithValue(message);
    }
  },
);

export const markAllNotificationsRead = createAsyncThunk(
  'notificationFeed/markAllRead',
  async (_, { dispatch, getState, rejectWithValue }) => {
    const state = getState() as RootState;
    const snapshot = {
      items: state.notificationFeed.items,
      unreadCount: state.notificationFeed.unreadCount,
    };
    dispatch(notificationFeedSlice.actions.applyMarkAllRead());
    try {
      await NotificationsService.postAppNotificationsReadAll();
      return true;
    } catch (err) {
      dispatch(notificationFeedSlice.actions.restoreFeed(snapshot));
      const message = err instanceof Error ? err.message : 'Failed to mark all read';
      dispatch(addNotification({
        type: 'error',
        title: i18next.t('notification_error'),
        message,
      }));
      return rejectWithValue(message);
    }
  },
);

const notificationFeedSlice = createSlice({
  name: 'notificationFeed',
  initialState,
  reducers: {
    applyMarkRead(state, action: PayloadAction<string>) {
      const id = action.payload;
      const row = state.items.find((item) => item.id === id);
      if (row && !row.isRead) {
        row.isRead = true;
        state.unreadCount = Math.max(0, state.unreadCount - 1);
      }
    },
    applyMarkAllRead(state) {
      state.items.forEach((item) => {
        item.isRead = true;
      });
      state.unreadCount = 0;
    },
    restoreFeed(
      state,
      action: PayloadAction<{ items: NotificationView[]; unreadCount: number }>,
    ) {
      state.items = action.payload.items;
      state.unreadCount = action.payload.unreadCount;
    },
    resetNotificationFeed() {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchNotificationFeed.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchNotificationFeed.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.items;
        state.unreadCount = action.payload.unreadCount;
        state.itemsLoaded = action.payload.itemsLoaded;
      })
      .addCase(fetchNotificationFeed.rejected, (state) => {
        state.loading = false;
      })
      .addCase(fetchNotificationBadge.fulfilled, (state, action) => {
        state.unreadCount = action.payload.unreadCount;
      });
  },
});

export const { resetNotificationFeed } = notificationFeedSlice.actions;
export default notificationFeedSlice.reducer;
